import graphqlResource, { GraphqlResource } from 'modules/core/graphql-resource';
import { Game } from 'modules/games/domain/interfaces/game';
import { TeamDto } from 'modules/teams/domain/dtos/team.dto';
import { teamStatisticsDtoDataMapper } from 'modules/championship/domain/data-mappers/team-statistics-dto.data-mapper';
import { FetchStageStatisticDto } from 'modules/championship/domain/interfaces/response-dto';
import { TeamGroupStatisticsRow } from 'modules/championship/domain/interfaces/team-group-statistics';
import { ResponseGraphql } from 'shared/types/response-graphql';
import { parseResponseError } from 'shared/utils';
import { mapGameDtoToGame, mapTeamDtoToTeam } from 'modules/games/domain/data-mappers/game.data-mapper';
import { compareTeamGroups } from '../utils/compare-team-groups';
import { StageType } from '../enums/stage-type';
import { Stage } from '../interfaces/stage';
import { StageDto } from '../interfaces/stage-dto';
import { TournamentBracket } from '../interfaces/tournament-bracket';
import { TournamentBracketDto } from '../interfaces/tournament-bracket-dto';
import { UpsertStageResult } from '../interfaces/upsert-stage-result';

/** Этапы соревнования вместе с их сетками плей-офф.
 *
 * Сетки не выделены в отдельный репозиторий намеренно: backend отдаёт их
 * вложенными в `stage`, поэтому `fetchStageBrackets` возвращает `Stage`, и на
 * него заканчиваются `upsertChampionshipStage`, `generateStageMatches` и
 * `recreateTournamentBrackets`. Отдельный bracket-репозиторий мог бы
 * существовать только как зависимость от этого. */

const findBracketSizeByTeamsCount = (teamsCount: number): number => {
  if (teamsCount === 0) return 0;

  let count = 2;
  while (count < teamsCount) {
    count *= 2;
  }

  return count - 1;
};

type ChampionshipStagesResponse = {
  data: {
    championship: {
      stages: StageDto[];
    };
  };
};

export interface UpsertChampionshipStageParams {
  id?: number;
  championshipId: number;
  name: string;
  type: StageType;
  teamIds?: number[];
  bracketSize?: number;
}

interface UpsertChampionshipStageResponse {
  data: {
    upsertStage: {
      id: number;
      name: string;
      championship: { id: number };
      type: StageType;
      teams?: TeamDto[];
      createdAt: string;
      updatedAt: string;
    };
  };
}

interface DeleteChampionshipStageResponse {
  data: {
    deleteStage: {
      status: string;
    };
  };
}

export interface SetTournamentBracketParams {
  tournamentBracketId: number;
  team1Id: number | null;
  team2Id: number | null;
}

type SetTournamentBracketResponse = {
  data: {
    setTournamentBracketTeams: TournamentBracketDto;
  };
};

export interface SetStageBracketsTeams {
  stageId: number;
  brackets: SetTournamentBracketParams[];
}

class StageRepository {
  constructor(private readonly graphqlResource: GraphqlResource) {}

  public async fetchChampionshipStages(championshipId: number): Promise<Stage[]> {
    const query = `#graphql
      query getChampionshipStages($id: Int!) {
        championship(id: $id) {
          stages {
            id
            name
            championship { id }
            type
            rounds {
              id
              name
              matches {
                id
                sportType
                date
                teams {
                  id
                  name
                  emblem {
                    id
                    externalUrl
                  }
                }
                teamMatches {
                  team { id }
                  score
                }
                matchEvents { id }
                round {
                  id
                  name
                  stage { id }
                }
                location {
                  id
                  name
                }
              }
              createdAt
              updatedAt
            }
            teams {
              id
              name
              emblem {
                id
                externalUrl
              }
            }
            teamGroups {
              id
              name
              stage { id }
              teams {
                id
                name
                emblem { externalUrl }
              }
              groupChampionshipTable {
                rows {
                  position
                  team { id }
                  gamesPlayed
                  gamesWon
                  gamesDraw
                  gamesLost
                  goalsWon
                  goalsLost
                  championshipScore
                }
              }
              sortOrder
              createdAt
              updatedAt
            }
            tournamentBrackets {
              id
              rank
              match {
                teams {
                  id
                  name
                  emblem {
                    externalUrl
                  }
                }
                teamMatches {
                  team { id }
                  teamOrder
                  score
                }
                location {
                  id
                  name
                }
              }
              round {
                id
                name
              }
              placeholder1
              placeholder2
              group
              childBracket { id }
              parentBrackets { id }
            }
            createdAt
            updatedAt
          }
        }
      }
    `;

    const response = await this.graphqlResource.query<ChampionshipStagesResponse>(query, {
      id: championshipId,
    });

    const { stages: championshipStages } = response.data.data.championship;

    return championshipStages.map((item) => {
      const stageMatches: Game[] = [];

      const rounds = item.rounds.map((roundItem) => {
        const matchIds: number[] = [];

        roundItem.matches.forEach((match) => {
          const game = mapGameDtoToGame(match);

          matchIds.push(match.id);
          stageMatches.push(game);
        });

        stageMatches.sort((a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf());

        return {
          id: roundItem.id,
          stageId: roundItem.id,
          name: roundItem.name,
          matchIds,
          createdAt: roundItem.createdAt,
          updatedAt: roundItem.updatedAt,
        };
      });

      const teamGroups = item.teamGroups
        .filter((teamGroup) => teamGroup.stage.id === item.id)
        .map((teamGroup) => {
          const groupTeams = teamGroup.teams.map((team) => mapTeamDtoToTeam(team, null)).filter(Boolean);

          const statistics = teamGroup.groupChampionshipTable.rows.map((row) => ({
            position: row.position,
            teamId: row.team.id,
            gamesPlayed: row.gamesPlayed,
            gamesWon: row.gamesWon,
            gamesLost: row.gamesLost,
            gamesDraw: row.gamesDraw,
            goalsWon: row.goalsWon,
            goalsLost: row.goalsLost,
            score: row.championshipScore,
          }));

          return {
            id: teamGroup.id,
            name: teamGroup.name,
            stageId: teamGroup.stage.id,
            teams: groupTeams,
            statistics,
            sortOrder: teamGroup.sortOrder ?? null,
            createdAt: teamGroup.createdAt,
            updatedAt: teamGroup.updatedAt,
          };
        })
        .sort(compareTeamGroups);

      const teams = item.teams ? item.teams.map((team) => mapTeamDtoToTeam(team, null)) : [];

      const brackets = (item.tournamentBrackets || []).map((bracket) => ({
        id: bracket.id,
        stageId: item.id,
        roundId: bracket.round.id,
        rank: bracket.rank,
        group: bracket.group,
        childBracketId: bracket.childBracket?.id || null,
        parentBracketIds: bracket.parentBrackets.map((parent) => parent.id),
        placeholder1: bracket.placeholder1,
        placeholder2: bracket.placeholder2,
        match: !bracket.match
          ? null
          : {
              id: bracket.match.id,
              teamMatches: bracket.match.teamMatches.map((tm) => {
                const team = !tm.team ? null : teams.find((t) => t.id === tm.team.id);

                return {
                  team,
                  teamOrder: tm.teamOrder,
                  score: tm.score,
                };
              }),
            },
      }));

      return {
        id: item.id,
        championshipId: item.championship.id,
        name: item.name,
        type: item.type,
        rounds,
        teams,
        teamGroups,
        matches: stageMatches,
        brackets,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      } as Stage;
    });
  }

  public async upsertChampionshipStage(params: UpsertChampionshipStageParams): Promise<UpsertStageResult> {
    const query = `#graphql
      mutation upsertStage($input: UpsertStageInput!) {
        upsertStage(input: $input) {
          id
          name
          championship { id }
          type
          teams {
            id
            name
            shortName
            emblem {
              id
              externalUrl
            }
          }
          createdAt
          updatedAt
        }
      }
    `;

    const bracketSize =
      params.type === StageType.SINGLE_ELIMINATION && params.teamIds ? findBracketSizeByTeamsCount(params.teamIds.length) : undefined;

    const variables = {
      input: {
        id: params.id,
        championshipId: params.championshipId,
        name: params.name,
        type: params.type,
        teamIds: params.teamIds,
      },
    };

    const response = await this.graphqlResource.query<UpsertChampionshipStageResponse>(query, variables);
    const result = response.data.data.upsertStage;

    if (bracketSize !== undefined) {
      return await this.recreateTournamentBrackets(result.id, bracketSize);
    }

    return { stage: await this.fetchStageBrackets(result.id), bracketsUpdateSkipped: false };
  }

  public async fetchStageBrackets(stageId: number): Promise<Stage> {
    const query = `#graphql
      query getStageBrackets($stageId: Int!) {
        stage(id: $stageId) {
          id
          name
          championship { id }
          type
          createdAt
          updatedAt
          rounds {
            id
            name
            matches {
              id
              sportType
              date
              teams {
                id
                name
                emblem {
                  id
                  externalUrl
                }
              }
              teamMatches {
                team { id }
                score
              }
              matchEvents { id }
              round {
                id
                name
                stage { id }
              }
              location {
                id
                name
              }
            }
            createdAt
            updatedAt
          }
          teams {
            id
            name
            shortName
            emblem {
              id
              externalUrl
            }
          }
          teamGroups {
            id
            name
            stage { id }
            teams {
              id
              name
              shortName
              emblem { externalUrl }
            }
            groupChampionshipTable {
              rows {
                position
                team { id }
                gamesPlayed
                gamesWon
                gamesDraw
                gamesLost
                goalsWon
                goalsLost
                championshipScore
              }
            }
            sortOrder
            createdAt
            updatedAt
          }
          tournamentBrackets {
            id
            match {
              id
              teamMatches {
                teamOrder
                score
                team {
                  id
                  name
                  emblem {
                    id
                    externalUrl
                  }
                }
              }
            }
            rank
            childBracket { id }
            parentBrackets { id }
            stage { id }
            round { id }
            placeholder1
            placeholder2
            group
          }
        }
      }
    `;
    const variables = { stageId };

    type FetchStageBracketsResponse = {
      data: {
        stage: StageDto;
      };
    };

    const response = await this.graphqlResource.query<FetchStageBracketsResponse>(query, variables);
    const { stage: item } = response.data.data;

    const stageMatches: Game[] = [];
    const rounds = item.rounds.map((roundItem) => {
      const matchIds: number[] = [];

      roundItem.matches.forEach((match) => {
        const game = mapGameDtoToGame(match);

        matchIds.push(match.id);
        stageMatches.push(game);
      });

      stageMatches.sort((a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf());

      return {
        id: roundItem.id,
        stageId: roundItem.id,
        name: roundItem.name,
        matchIds,
        createdAt: roundItem.createdAt,
        updatedAt: roundItem.updatedAt,
      };
    });

    const teamGroups = item.teamGroups
      .filter((teamGroup) => teamGroup.stage.id === item.id)
      .map((teamGroup) => {
        const groupTeams = teamGroup.teams.map((team) => mapTeamDtoToTeam(team, null)).filter(Boolean);

        const statistics = teamGroup.groupChampionshipTable.rows.map((row) => ({
          position: row.position,
          teamId: row.team.id,
          gamesPlayed: row.gamesPlayed,
          gamesWon: row.gamesWon,
          gamesLost: row.gamesLost,
          gamesDraw: row.gamesDraw,
          goalsWon: row.goalsWon,
          goalsLost: row.goalsLost,
          score: row.championshipScore,
        }));

        return {
          id: teamGroup.id,
          name: teamGroup.name,
          stageId: teamGroup.stage.id,
          teams: groupTeams,
          statistics,
          sortOrder: teamGroup.sortOrder ?? null,
          createdAt: teamGroup.createdAt,
          updatedAt: teamGroup.updatedAt,
        };
      })
      .sort(compareTeamGroups);

    const teams = item.teams ? item.teams.map((team) => mapTeamDtoToTeam(team, null)) : [];

    const brackets = (item.tournamentBrackets || []).map((bracket) => ({
      id: bracket.id,
      stageId: item.id,
      roundId: bracket.round.id,
      rank: bracket.rank,
      group: bracket.group,
      childBracketId: bracket.childBracket?.id || null,
      parentBracketIds: bracket.parentBrackets.map((parent) => parent.id),
      placeholder1: bracket.placeholder1,
      placeholder2: bracket.placeholder2,
      match: !bracket.match
        ? null
        : {
            id: bracket.match.id,
            teamMatches: bracket.match.teamMatches.map((tm) => {
              const team = !tm.team ? null : teams.find((t) => t.id === tm.team.id);

              return {
                team,
                teamOrder: tm.teamOrder,
                score: tm.score,
              };
            }),
          },
    }));

    return {
      id: item.id,
      championshipId: item.championship.id,
      name: item.name,
      type: item.type,
      rounds,
      teams,
      teamGroups,
      matches: stageMatches,
      brackets,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    } as Stage;
  }

  public async recreateTournamentBrackets(stageId: number, bracketSize: number): Promise<UpsertStageResult> {
    const query = `#graphql
      mutation recreateTournamentBrackets($stageId: Int!, $bracketSize: Int!) {
        recreateTournamentBrackets(stageId: $stageId, bracketSize: $bracketSize) {
          id
        }
      }
    `;

    const variables = {
      stageId,
      bracketSize,
    };

    const response = await this.graphqlResource.query<{ errors: Array<{ message: string }> }>(query, variables);

    // Сетку могли не пересоздать из-за слишком близкого матча. Этап при этом сохранён,
    // поэтому не бросаем ошибку, а отдаём флаг — сообщение покажет вызывающий код.
    let bracketsUpdateSkipped = false;

    if (response.data.errors) {
      if (response.data.errors.length === 1) {
        bracketsUpdateSkipped = response.data.errors[0].message.includes('Cannot delete match less than 5 hours before match');
      } else if (response.data.errors.length > 1) {
        for (const error of response.data.errors) {
          console.error(error.message);
        }
      }
    }

    return { stage: await this.fetchStageBrackets(stageId), bracketsUpdateSkipped };
  }

  public async generateStageMatches(stageId: number): Promise<Stage> {
    const mutation = `#graphql
      mutation generateStageMatches($stageId: Int!) {
        generateStageMatches(stageId: $stageId) {
          id
        }
      }
    `;

    const response = await this.graphqlResource.query<ResponseGraphql<{ generateStageMatches: { id: number } }>>(mutation, {
      stageId,
    });
    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    return this.fetchStageBrackets(stageId);
  }

  public async deleteChampionshipStage(stageId: number): Promise<boolean> {
    const query = `#graphql
      mutation deleteStage($id: Int!) {
        deleteStage(id: $id) {
          status
        }
      }
    `;

    const response = await this.graphqlResource.query<DeleteChampionshipStageResponse>(query, { id: stageId });
    const { status } = response.data.data.deleteStage;

    return status === 'ok';
  }

  public async setTournamentBracket(params: SetTournamentBracketParams): Promise<TournamentBracket> {
    const variables = { input: params };
    const query = `#graphql
      mutation setTournamentBracketTeams($input: SetTournamentBracketTeamsInput!) {
        setTournamentBracketTeams(input: $input) {
          id
          match {
            id
            teamMatches {
              teamOrder
              score
              team {
                id
                name
                shortName
                emblem {
                  id
                  externalUrl
                }
              }
            }
          }
          rank
          childBracket { id }
          parentBrackets { id }
          stage { id }
          round { id }
          placeholder1
          placeholder2
          group
        }
      }
    `;

    const response = await this.graphqlResource.query<SetTournamentBracketResponse>(query, variables);
    const bracket = response.data.data.setTournamentBracketTeams;

    return {
      id: bracket.id,
      stageId: bracket.stage.id,
      roundId: bracket.round.id,
      rank: bracket.rank,
      group: bracket.group,
      childBracketId: bracket.childBracket?.id || null,
      parentBracketIds: bracket.parentBrackets.map((parent) => parent.id),
      placeholder1: bracket.placeholder1,
      placeholder2: bracket.placeholder2,
      match: !bracket.match
        ? null
        : {
            id: bracket.match.id,
            teamMatches: bracket.match.teamMatches.map((tm) => {
              const team = tm.team ? mapTeamDtoToTeam(tm.team as TeamDto, null) : null;
              return {
                team,
                teamOrder: tm.teamOrder,
                score: tm.score,
              };
            }),
          },
    };
  }

  public async fetchStageStatistic(stageId: number): Promise<TeamGroupStatisticsRow[]> {
    const query = `#graphql
          query fetchStageStatistic($id: Int!) {
            stageChampionshipTable(stageId: $id) {
              rows {
                team { id }
                position
                gamesPlayed
                gamesWon
                gamesDraw
                gamesLost
                goalsWon
                goalsLost
                championshipScore
              }
            }
          }
        `;

    const response = await this.graphqlResource.query<ResponseGraphql<FetchStageStatisticDto>>(query, { id: stageId });

    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    const rows = response.data.data?.stageChampionshipTable?.rows;
    const result = rows?.filter((row) => row?.team?.id) ?? [];
    return result.map(teamStatisticsDtoDataMapper);
  }
}

const stageRepository = new StageRepository(graphqlResource);

export default stageRepository;
